import React, { useState } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { Download } from 'lucide-react';
import { saveAs } from 'file-saver';
import { buildPsychBedReportWorkbook, type PsychBedReport } from './psychBedReportWorkbook';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const REPORT = gql`query PsychBedReport($component: String!, $report: String!, $hearingDate: String) { psychBedReport(component: $component, report: $report, hearingDate: $hearingDate) { title fields labels sequentialIds rows { cells { key value } } } }`;
export const PsychBedReportDialog = ({ component, report, onClose }: { component: string; report: string; onClose: () => void }) => {
  const [hearingDate, setHearingDate] = useState('');
  const [password, setPassword] = useState('');
  const [downloadError, setDownloadError] = useState('');
  const [preparing, setPreparing] = useState(false);
  const [load, { loading, error }] = useLazyQuery<{ psychBedReport: PsychBedReport }>(REPORT, { fetchPolicy: 'network-only' });
  const busy = loading || preparing;
  const download = async () => {
    setPreparing(true);
    setDownloadError('');
    try {
      const result = await load({ variables: { component, report, hearingDate: report === 'court' ? hearingDate : null } });
      const payload = result.data?.psychBedReport;
      if (!payload || result.error) throw new Error('The report could not be downloaded.');
      const bytes = await buildPsychBedReportWorkbook(payload, password);
      saveAs(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${component}_${report}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      onClose();
    } catch {
      setDownloadError('The report could not be downloaded. Please try again.');
    } finally {
      setPreparing(false);
    }
  };
  return <Dialog open onOpenChange={(open: boolean) => { if (!open && !busy) onClose(); }}>
    <DialogContent className="max-w-lg" onPointerDownOutside={(event) => event.preventDefault()}>
      <DialogHeader><DialogTitle>{report === 'court' ? 'Court List' : 'Psych Bed Report'}</DialogTitle></DialogHeader>
      {report === 'court' && <><label htmlFor="psych-bed-report-date">Hearing date</label><Input id="psych-bed-report-date" type="date" required value={hearingDate} onChange={(event) => setHearingDate(event.target.value)} /></>}
      <label htmlFor="psych-bed-report-password">Report protection password (optional)</label>
      <Input id="psych-bed-report-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
      {(error || downloadError) && <p role="alert" className="text-[var(--semantic-error)]">{downloadError || 'The report could not be downloaded. Please try again.'}</p>}
      <div className="flex justify-end gap-2"><Button id="psych-bed-report-download" disabled={busy || (report === 'court' && !hearingDate)} onClick={() => { void download(); }}><Download className="h-4 w-4" />{busy ? 'Preparing…' : 'Download'}</Button><Button id="psych-bed-report-close" variant="outline" disabled={busy} onClick={onClose}>Close</Button></div>
    </DialogContent>
  </Dialog>;
};
